/**
 * GameServices — Unified Game Backend
 *
 * Shared Supabase integration for all MISK Tech games.
 * Handles: leaderboards, player progression, cross-game XP.
 *
 * Tables required in Supabase:
 *   game_scores    — leaderboard entries
 *   game_profiles  — cross-game player progression
 *   game_events    — behavioral telemetry (Game of Fire)
 *
 * Graceful degradation: if Supabase is unavailable,
 * falls back to localStorage silently. Zero crashes.
 */

import { supabase } from './supabaseClient';

const LS_FALLBACK_KEY = 'misk_game_services_fallback';

// --- Helpers ---
function getFallback() {
  try { return JSON.parse(localStorage.getItem(LS_FALLBACK_KEY)) || {}; } catch { return {}; }
}
function setFallback(data) {
  try { localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify(data)); } catch {}
}

// --- Leaderboards ---

/**
 * Submit a score to the leaderboard.
 * @param {string} gameId — e.g., 'nexus-mayhem', 'grid-hunter'
 * @param {string} playerName — display name
 * @param {number} score — numeric score
 * @param {object} meta — optional metadata (wave, accuracy, combo, etc.)
 */
export async function submitScore(gameId, playerName, score, meta = {}) {
  try {
    const { error } = await supabase.from('game_scores').insert({
      game_id: gameId,
      player_name: playerName,
      score,
      meta,
      created_at: new Date().toISOString(),
    });
    if (error) throw error;
    return { ok: true };
  } catch (err) {
    // Fallback to localStorage
    const fb = getFallback();
    if (!fb.scores) fb.scores = [];
    fb.scores.push({ game_id: gameId, player_name: playerName, score, meta, created_at: new Date().toISOString() });
    if (fb.scores.length > 500) fb.scores.splice(0, fb.scores.length - 500);
    setFallback(fb);
    return { ok: false, fallback: true, error: err.message };
  }
}

/**
 * Get top scores for a game.
 * @param {string} gameId
 * @param {number} limit — default 10
 */
export async function getLeaderboard(gameId, limit = 10) {
  try {
    const { data, error } = await supabase
      .from('game_scores')
      .select('player_name, score, meta, created_at')
      .eq('game_id', gameId)
      .order('score', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data || [];
  } catch {
    // Fallback
    const fb = getFallback();
    const scores = (fb.scores || []).filter(s => s.game_id === gameId);
    scores.sort((a, b) => b.score - a.score);
    return scores.slice(0, limit);
  }
}

/**
 * Get personal best for a player in a game.
 */
export async function getPersonalBest(gameId, playerName) {
  try {
    const { data, error } = await supabase
      .from('game_scores')
      .select('score, meta, created_at')
      .eq('game_id', gameId)
      .eq('player_name', playerName)
      .order('score', { ascending: false })
      .limit(1);
    if (error) throw error;
    return data?.[0] || null;
  } catch {
    const fb = getFallback();
    const scores = (fb.scores || []).filter(s => s.game_id === gameId && s.player_name === playerName);
    scores.sort((a, b) => b.score - a.score);
    return scores[0] || null;
  }
}

// --- Player Progression ---

/**
 * XP thresholds per level (exponential curve).
 * Level 1: 0 XP, Level 2: 100 XP, Level 3: 250 XP, etc.
 */
function xpForLevel(level) {
  if (level <= 1) return 0;
  return Math.floor(100 * Math.pow(1.3, level - 2));
}

function calcLevel(totalXp) {
  let level = 1;
  let threshold = 0;
  while (true) {
    const next = xpForLevel(level + 1);
    if (totalXp < threshold + next) break;
    threshold += next;
    level++;
    if (level > 100) break; // cap
  }
  return { level, currentXp: totalXp - threshold, nextLevelXp: xpForLevel(level + 1) };
}

/**
 * Get or create a player profile.
 * @param {string} playerId — unique identifier (email, anon ID, etc.)
 */
export async function getProfile(playerId) {
  try {
    const { data, error } = await supabase
      .from('game_profiles')
      .select('*')
      .eq('player_id', playerId)
      .single();
    if (error && error.code === 'PGRST116') {
      // Not found — create new profile
      const newProfile = {
        player_id: playerId,
        display_name: playerId,
        total_xp: 0,
        games_played: 0,
        total_play_time: 0,
        achievements: [],
        game_stats: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { data: created, error: createErr } = await supabase
        .from('game_profiles')
        .insert(newProfile)
        .select()
        .single();
      if (createErr) throw createErr;
      return { ...created, ...calcLevel(created.total_xp) };
    }
    if (error) throw error;
    return { ...data, ...calcLevel(data.total_xp) };
  } catch {
    // Fallback
    const fb = getFallback();
    if (!fb.profile) {
      fb.profile = { player_id: playerId, display_name: playerId, total_xp: 0, games_played: 0, total_play_time: 0, achievements: [], game_stats: {} };
      setFallback(fb);
    }
    return { ...fb.profile, ...calcLevel(fb.profile.total_xp) };
  }
}

/**
 * Award XP to a player after a game session.
 * @param {string} playerId
 * @param {number} xp — XP to add
 * @param {string} gameId — which game awarded it
 * @param {object} sessionStats — game-specific stats to merge
 */
export async function awardXp(playerId, xp, gameId, sessionStats = {}) {
  try {
    // Get current profile
    const { data: profile, error: getErr } = await supabase
      .from('game_profiles')
      .select('total_xp, games_played, total_play_time, game_stats')
      .eq('player_id', playerId)
      .single();

    if (getErr) throw getErr;

    const gameStats = profile.game_stats || {};
    if (!gameStats[gameId]) gameStats[gameId] = { sessions: 0, totalXp: 0, bestScore: 0 };
    gameStats[gameId].sessions++;
    gameStats[gameId].totalXp += xp;
    if (sessionStats.score && sessionStats.score > gameStats[gameId].bestScore) {
      gameStats[gameId].bestScore = sessionStats.score;
    }
    Object.assign(gameStats[gameId], sessionStats);

    const newXp = (profile.total_xp || 0) + xp;
    const { error: updateErr } = await supabase
      .from('game_profiles')
      .update({
        total_xp: newXp,
        games_played: (profile.games_played || 0) + 1,
        game_stats: gameStats,
        updated_at: new Date().toISOString(),
      })
      .eq('player_id', playerId);

    if (updateErr) throw updateErr;

    const levelInfo = calcLevel(newXp);
    const prevLevel = calcLevel(newXp - xp);
    return { ok: true, ...levelInfo, leveledUp: levelInfo.level > prevLevel.level, xpAwarded: xp };
  } catch {
    // Fallback
    const fb = getFallback();
    if (!fb.profile) fb.profile = { player_id: playerId, total_xp: 0, games_played: 0, game_stats: {} };
    fb.profile.total_xp += xp;
    fb.profile.games_played++;
    if (!fb.profile.game_stats) fb.profile.game_stats = {};
    if (!fb.profile.game_stats[gameId]) fb.profile.game_stats[gameId] = { sessions: 0, totalXp: 0, bestScore: 0 };
    fb.profile.game_stats[gameId].sessions++;
    fb.profile.game_stats[gameId].totalXp += xp;
    setFallback(fb);
    const levelInfo = calcLevel(fb.profile.total_xp);
    return { ok: false, fallback: true, ...levelInfo, xpAwarded: xp };
  }
}

// --- Behavioral Telemetry (Game of Fire) ---

/**
 * Save a GeniusEngine session to Supabase.
 */
export async function saveGeniusSession(playerId, missionId, sessionData) {
  try {
    const { error } = await supabase.from('game_events').insert({
      player_id: playerId,
      game_id: 'game-of-fire',
      event_type: 'genius_session',
      mission_id: missionId,
      data: sessionData,
      created_at: new Date().toISOString(),
    });
    if (error) throw error;
    return { ok: true };
  } catch {
    return { ok: false, fallback: true };
  }
}

// --- Utility ---

/**
 * Calculate XP reward for a game session.
 */
export function calculateSessionXp(gameId, stats) {
  let xp = 0;

  switch (gameId) {
    case 'nexus-mayhem':
      xp = Math.floor(stats.score / 10); // 1 XP per 10 points
      xp += (stats.wave || 1) * 15;      // 15 XP per wave survived
      xp += (stats.kills || 0) * 2;      // 2 XP per kill
      break;

    case 'grid-hunter':
      xp = Math.floor(stats.score / 10);
      xp += (stats.wave || 1) * 10;
      xp += Math.floor((stats.accuracy || 0) * 0.5); // bonus for accuracy
      xp += (stats.maxCombo || 0) * 3;
      break;

    case 'game-of-fire':
      xp = stats.xpAwarded || 50; // mission XP from outcome
      xp += (stats.geniusFlags || []).length * 25; // genius bonus
      xp += (stats.explorationScore || 0);
      break;

    default:
      xp = Math.floor(stats.score / 10) + 10;
  }

  return Math.max(10, xp); // minimum 10 XP
}

export { calcLevel, xpForLevel };
